import { useEffect, useState } from 'react';

import { fetchQuery, graphql, useRelayEnvironment } from 'react-relay';

import { useErrorAnalytics } from '../analytics/use-error-analytics';
import type { useGetIsCollectionQuery } from './__generated__/useGetIsCollectionQuery.graphql';
import { type EditionAwarenessContextType, useEditionAwareness } from './edition-awareness-context';
import { getHamsKey } from './product-to-hams-key';

type IsCollection = NonNullable<EditionAwarenessContextType['isInCollection']>;

type CollectionData = {
	data: IsCollection | null;
	error: Error | null;
	loading: boolean;
};

export function useGetIsCollection(): CollectionData {
	const { cloudId, product, edition } = useEditionAwareness();
	const environment = useRelayEnvironment();
	const reportError = useErrorAnalytics({ product, edition, id: 'use-get-is-collection' });

	const [returnData, setReturnData] = useState<CollectionData>({
		data: null,
		loading: true,
		error: null,
	});

	useEffect(() => {
		fetchQuery<useGetIsCollectionQuery>(
			environment,
			graphql`
				query useGetIsCollectionQuery($cloudId: ID!, $productKey: String!) {
					tenantContexts(cloudIds: [$cloudId]) {
						entitlementInfo(hamsProductKey: $productKey) {
							... on CommerceEntitlementInfoCcp {
								entitlement {
									relatesFromEntitlements(relationshipTypes: "COLLECTION") {
										relationshipType
										entitlementId
									}
								}
							}
						}
					}
				}
			`,
			{ cloudId, productKey: getHamsKey(product) },
		)
			.toPromise()
			.then((data) => {
				const relationships =
					data?.tenantContexts?.[0]?.entitlementInfo?.entitlement?.relatesFromEntitlements;

				if (!relationships) {
					return setReturnData({ data: 'UNKNOWN', loading: false, error: null });
				}

				const isCollection = relationships.some(
					(relationship) =>
						relationship?.relationshipType === 'COLLECTION' && relationship.entitlementId,
				)
					? 'IN_COLLECTION'
					: 'NOT_IN_COLLECTION';

				return setReturnData({ data: isCollection, loading: false, error: null });
			})
			.catch((e: unknown) => {
				const error = e instanceof Error ? e : new Error(String(e));
				reportError(error);
				setReturnData({ data: null, loading: false, error });
			});
	}, [cloudId, environment, product, reportError]);

	return returnData;
}
